/**
 * Lista inline de anuncios tipo `card`.
 *
 * A diferencia del modal y banner (globales en Layout), las cards se renderizan
 * inline encima del contenido de la página. Layout monta este componente arriba
 * del <Outlet />, lo que las hace aparecer en cualquier ruta del panel sin
 * cambiar la estructura interna de cada página.
 *
 * Comparte el fetch global con CommunicationsCenter — ambos consumen el mismo
 * useGetAnnouncement. La caché vive en el hook (módulo de servicios de axios),
 * así que dos consumidores no disparan dos GETs simultáneos en el mismo render.
 */

import { useCallback, useMemo, useState } from 'react';
import { Button } from '@plick/ui';
import { useGetAnnouncement } from './CommunicationsModal/hooks/useGetAnnouncement';
import { CommunicationsCard } from './CommunicationsCard/CommunicationsCard';

const PREVIEW_LIMIT = 3;

export const AnnouncementCardList = () => {
  const { announcements, isFetching } = useGetAnnouncement();
  const [expanded, setExpanded] = useState(false);

  // Dismiss solo de sesión: la fuente de verdad es el back vía show_once /
  // user_response. Si show_once=false la card vuelve a aparecer al recargar
  // (decisión del operador del back, no del comercio).
  const [sessionDismissed, setSessionDismissed] = useState<Set<string>>(new Set());
  const dismissForSession = useCallback((uuid: string) => {
    setSessionDismissed((prev) => {
      if (prev.has(uuid)) return prev;
      const next = new Set(prev);
      next.add(uuid);
      return next;
    });
  }, []);

  const cards = useMemo(
    () => announcements.filter((a) => a.announcement_type === 'card' && !sessionDismissed.has(a.uuid)),
    [announcements, sessionDismissed],
  );

  if (isFetching || cards.length === 0) return null;

  const visible = expanded ? cards : cards.slice(0, PREVIEW_LIMIT);
  const hasMore = cards.length > PREVIEW_LIMIT;

  // Floating top-right del area de contenido: la lista no desplaza el header
  // ni el body de la pagina (el padre en Layout.tsx es `relative` para anclar).
  // En mobile pasa a ancho completo para no chocar con CTAs estrechos.
  //
  // max-h + overflow-y-auto: como `position: absolute` saca al elemento del
  // flujo, el `overflow-auto` del padre no genera scrollbar para esta lista.
  // Cuando el comercio expande "Ver mas" en viewports chicos, sin esta cota
  // las cards extra quedan clippeadas e inaccesibles.
  return (
    <section
      aria-label="Anuncios"
      className="absolute top-3 right-3 z-30 max-w-sm w-[calc(100%-1.5rem)]
                 max-h-[calc(100%-1.5rem)] overflow-y-auto
                 max-md:left-3 flex flex-col gap-3 pointer-events-auto"
    >
      <div className="flex flex-col gap-3">
        {visible.map((card) => (
          <CommunicationsCard
            key={card.uuid}
            announcement={card}
            onDismiss={() => dismissForSession(card.uuid)}
            onRespond={() => dismissForSession(card.uuid)}
          />
        ))}
      </div>

      {hasMore && (
        <div className="self-center">
          <Button.Secondary
            text={expanded ? 'Mostrar menos' : `Ver ${cards.length - PREVIEW_LIMIT} más`}
            onClick={() => setExpanded((v) => !v)}
          />
        </div>
      )}
    </section>
  );
};
